import { Component, Input, OnDestroy, OnInit, EventEmitter } from '@angular/core';
import { 
  WorkflowInstance,
  TaskDefinition,
  TaskInstance,
} from '@models/workflow.model';
import { User } from '@models/user.model';
import { PurchaseOrderDto } from '@app/models/purchase-order.model.dto';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { CompanyService } from '@services/company.service';
import { Toaster } from '@services/toaster.service';
import { LocalStorage } from 'ngx-webstorage';
import CONFIG from '@app/app.config';
import * as moment from 'moment';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import Swal from 'sweetalert2';

@Component({
  selector: 'ngx-task-instance',
  templateUrl: './task-instance-modal.component.html',
  styleUrls: ['./task-instance-modal.component.scss'],
})
export class TaskInstanceModalComponent implements OnInit, OnDestroy {
  readonly FILE_LIMIT: number = 25000000;

  @LocalStorage() role: string;

  @Input() organizationId;
  @Input() workflow: WorkflowInstance;
  @Input() task: TaskInstance;
  @Input() approvalTask: TaskInstance;
  @Input() po: PurchaseOrderDto;
  @Input() reloadWorkflowInstanceEvent: EventEmitter<any>;

  // @Input() users: User[];
  @Input() tasks: TaskInstance[];

  loading: boolean = false;
  isEditionMode: boolean = false;

  selectedFormView: string = 'task';

  entityValue: string;

  user$: Observable<User>;
  currentUser: User;

  constructor(
    private ngbActiveModal: NgbActiveModal,
    private store: Store<{ user: User }>,
    private $company: CompanyService,
    private $toaster: Toaster,
  ) {
  }

  ngOnInit() {
    this.loading = true;
    let f = function () {this.loading = false;}
    setTimeout(f.bind(this), 500);

    this.user$ = this.store.select('user');
    this.user$
      .subscribe((user) => {
        this.currentUser = (user as User);
      });

  }

  getTask(taskId) {
    return this.tasks.find(t => t.instance_id === taskId);
  }

  goToTask(taskId, approvalTask = null) {
    let task = this.getTask(taskId);
    if (!task) {
      this.$toaster.show('error', 'An error occurred', 'Please try again later');
      return;
    }
    this.loading = true;
    let f = function (task) {
              this.loading = false;
              this.task = task;
              this.approvalTask = approvalTask;
            }
    setTimeout(f.bind(this, task), 500);
  }

  edit() {
    this.isEditionMode = true;
  }

  save() {

  }

  isTaskExpired(task: TaskInstance) {
    let now = moment();
    if (!!task?.completed_by) {
      return false;
    }
    if (moment.utc(task.due_date).isBefore(now)) {
      return true;
    }

    return false;
  }

  getCtaText() {
    let task = this.task;
    if (task.active) {
      return task.task_type.action_instructions.concat('. Correlated resource or action: ', task.entity.name || 'N/A');
    }
    if (task.is_dependency_completed === false && task.depends_on_task_instance_id !== null) {
      return 'You cannot complete this task because the task upon which it depends has not been completed.'
    }
    if (
        task.allow_anyone_to_complete === false &&
        (task.responsible.id !== this.currentUser.id &&
        task.responsible.organization_id !== this.currentUser.organization_id)
    ) {
      return 'You cannot complete this task because you are not the assigned responsible nor from the same organization.'
    }
    if (!task.active) {
      return 'You cannot complete this task.'
    }
    return task.task_type.action_instructions.concat('. Correlated resource or action:', task.entity.name || 'N/A');
  }

  handleCtaButtonClick($event, isDone: boolean = null) {
    let task = this.task;

    let payload: any = {};

    switch(task.task_type.interaction_type) {
      case 'simple':
        payload.is_done = isDone;
        break;
      case 'submit':
        payload.input = this.entityValue;
        break;
      case 'approve':
        this.goToTask(task.affected_task_instance_id, task);
        return;
      case 'upload':
        document.getElementById('file-input').click();
        return;
    }

    this.$company.completeTaskInstance(
      this.workflow.id,
      this.workflow.instance_id,
      task.id,
      task.instance_id,
      payload,
    ).subscribe(res => {
      this.$toaster.show('success', 'Success', 'You completed the task.');
      this.reloadWorkflowInstanceEvent.emit({open_task_id: task.instance_id});
      this.loading = true;
      this.closeModal();
    }, error => {
      console.error(error);
      this.$toaster.show('error', 'An error occurred', 'Please try again later.');
    });

  }

  approveTask() {
    this.completeTaskWithApproval(true);
  }

  async rejectTask() {
    const { value: text } = await Swal.fire({
      input: 'textarea',
      inputLabel: 'Please provide a rejection reason',
      inputPlaceholder: 'Type here...',
      inputAttributes: {
        'aria-label': 'Type here...'
      },
      cancelButtonText: 'Cancel',
      showCancelButton: true,
      buttonsStyling: false,
      confirmButtonText: 'Continue',
      customClass: {
        confirmButton: 'btn btn-primary mr-2',
        cancelButton: 'btn btn-secondary ml-2',
      },
      confirmButtonColor: '#41b146',
    });

    if (text) {
      this.completeTaskWithApproval(false, text);
    }
  }

  completeTaskWithApproval(approved: boolean, reason: string = null) {
    if (!this.approvalTask) {
      this.$toaster.show('error', 'An error occurred', 'Please try again later.');
      return;
    }

    this.$company.completeTaskInstance(
      this.workflow.id,
      this.workflow.instance_id,
      this.approvalTask.id,
      this.approvalTask.instance_id,
      {is_approved: approved, rejection_reason: reason},
    ).subscribe(res => {
      let action = approved === true ? 'approved' : 'rejected';
      this.$toaster.show('success', 'Success', `You ${action} the task.`);

      this.reloadWorkflowInstanceEvent.emit({open_task_id: this.approvalTask.instance_id});
      this.loading = true;
      this.closeModal();
    }, error => {
      console.error(error);
      this.$toaster.show('error', 'An error occurred', 'Please try again later.');
    });
  }

  completeTaskWithFile($event) {
    let task = this.task;
    
    const fileList: FileList = $event.srcElement.files;
    const formData: FormData = new FormData();

    if (fileList.length === 0) {
      return;
    }

    for (let i = 0; i < fileList.length; i++) {
      const file = fileList.item(i);
      if (file.size > this.FILE_LIMIT) {
        this.$toaster.show('warning', 'File size', 'Maximum file size for uploads is 25MB');
        return;
      }
      formData.append('file', file, file.name);
    }

    this.$company.completeTaskInstanceWithFile(
      this.workflow.id,
      this.workflow.instance_id,
      task.id,
      task.instance_id,
      formData,
    ).subscribe(res => {
      this.$toaster.show('success', 'Success', 'You completed the task.');
      this.reloadWorkflowInstanceEvent.emit({open_task_id: task.instance_id});
      this.loading = true;
      let f = function () {this.closeModal();}
      setTimeout(f.bind(this), 500);
    }, error => {
      console.error(error);
      this.$toaster.show('error', 'An error occurred', 'Please try again later.');
    });
  }

  getUserTimezone() {
    let tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return tz ? tz : 'utc';
  }

  closeModal() {
    this.ngbActiveModal.dismiss();
  }

  ngOnDestroy(): void {
  }

}
